import { useEffect, useMemo, useRef, useState } from 'react'
import { agentLabel, type Member, type Message, type MessageKind, type MessageTarget } from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

const KIND_LABEL: Record<MessageKind, string> = {
  chat: '',
  question: '質問',
  answer: '回答',
  decision: '決定',
  system: 'システム',
}

function timeOf(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function dayOf(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today.getTime() - 86_400_000)
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  if (same(d, today)) return '今日'
  if (same(d, yesterday)) return '昨日'
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

/**
 * How much of a message is shown before it is folded.
 *
 * Measured from the real room rather than guessed: six messages accounted for 70%
 * of all the text in it, every one of them an agent's answer running to three
 * thousand characters. Folding those is the single change that makes the timeline
 * readable — far more than archiving finished threads, which turned out to be 4%.
 */
const FOLD_LINES = 8
const FOLD_CHARS = 420

function MessageBody({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const lines = text.split('\n')
  const long = lines.length > FOLD_LINES || text.length > FOLD_CHARS
  if (!long || open) {
    return (
      <>
        <div className="msg-body">{text}</div>
        {long && (
          <button className="text" onClick={() => setOpen(false)}>
            折りたたむ
          </button>
        )}
      </>
    )
  }
  const head = lines.slice(0, FOLD_LINES).join('\n').slice(0, FOLD_CHARS)
  return (
    <>
      <div className="msg-body is-folded">{head}</div>
      <button className="text" onClick={() => setOpen(true)}>
        全文を表示（{text.length.toLocaleString()} 文字）
      </button>
    </>
  )
}

interface Thread {
  root: Message
  replies: Message[]
  resolved: boolean
  /** Newest timestamp in the thread, so a reply brings its question back up. */
  at: string
}

type Filter = 'all' | 'open' | 'mine'

export function Timeline({
  room,
  me,
  messages,
  members,
  onChanged,
}: {
  room: string
  me: Member
  messages: Message[]
  members: Member[]
  onChanged: () => void
}) {
  const [body, setBody] = useState('')
  const [kind, setKind] = useState<MessageKind>('chat')
  // One control for "who", covering both the person and their Claude. They are the
  // same identity, so this is a facet of the recipient rather than a separate one.
  const [recipient, setRecipient] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [held, setHeld] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const [limit, setLimit] = useState(50)
  const [openThread, setOpenThread] = useState<string | null>(null)
  const bottom = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  /**
   * Group answers under the question they answer.
   *
   * The link already exists — `in_reply_to` is set whenever somebody answers — it
   * was just never used for display, so a question and its answer sat apart with
   * unrelated chat between them. Grouping also makes "finished" a property of a
   * whole exchange, which is what lets a resolved one collapse to a single line.
   */
  const threads = useMemo<Thread[]>(() => {
    const byId = new Map(messages.map((m) => [m.id, m]))
    const replies = new Map<string, Message[]>()
    const roots: Message[] = []

    for (const m of messages) {
      const parent = m.in_reply_to && byId.get(m.in_reply_to)
      if (parent) {
        const list = replies.get(parent.id) ?? []
        list.push(m)
        replies.set(parent.id, list)
      } else {
        roots.push(m)
      }
    }

    return roots.map((root) => {
      const kids = (replies.get(root.id) ?? []).sort((a, b) => a.seq - b.seq)
      const last = kids.length > 0 ? kids[kids.length - 1]! : root
      return {
        root,
        replies: kids,
        resolved: root.kind === 'question' && root.resolved_at !== null,
        at: last.created_at,
      }
    })
  }, [messages])

  const filtered = useMemo(() => {
    if (filter === 'open') {
      return threads.filter((t) => t.root.kind === 'question' && !t.resolved)
    }
    if (filter === 'mine') {
      return threads.filter((t) =>
        [t.root, ...t.replies].some((m) => m.mentions.includes(me.user_id)),
      )
    }
    return threads
  }, [threads, filter, me.user_id])

  const hidden = Math.max(0, filtered.length - limit)
  const visible = filtered.slice(hidden)

  const send = async () => {
    if (!body.trim()) return
    setBusy(true)
    setError(null)
    setHeld(null)
    try {
      const [toUser, toRole] = recipient ? recipient.split(':') : ['', 'any']
      const result = await api.post(room, {
        body: body.trim(),
        kind,
        mentions: toUser ? [toUser] : [],
        to_role: (toRole ?? 'any') as MessageTarget,
      })
      if (result.status === 'held') {
        // The hub refused to send it. Say so plainly rather than clearing the box —
        // the text is still the author's to fix or approve.
        setHeld(result.reason)
      } else if (result.status === 'duplicate') {
        setNotice('同じ質問がまだ回答待ちなので、重ねて送っていません。')
      } else {
        setBody('')
        setRecipient('')
        setKind('chat')
      }
      onChanged()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const resolve = async (id: string) => {
    try {
      await api.resolve(id)
      onChanged()
    } catch (err) {
      setError(describeError(err))
    }
  }

  /**
   * Take back your own message. The result is reported as it actually happened:
   * telling someone "deleted" when the other company already has the text would be
   * a comfortable lie at exactly the moment they need the truth.
   */
  const retract = async (id: string) => {
    if (!window.confirm('この発言を取り消しますか？')) return
    try {
      const result = await api.retractMessage(room, id)
      if (result.status === 'purged') {
        setNotice('まだ誰も受け取っていなかったため、完全に削除しました。')
      } else {
        const who = result.seen_by.map((r) => `${r.display_name} (${r.org})`).join(' / ')
        setNotice(
          `本文は削除しましたが、${who} は既に受け取っています。` +
            '相手が読んだ内容までは取り消せません。必要なら直接フォローしてください。',
        )
      }
      onChanged()
    } catch (err) {
      setError(describeError(err))
    }
  }

  const others = members.filter((m) => m.user_id !== me.user_id)

  const renderMessage = (m: Message, isReply = false) => {
    const isQuestion = m.kind === 'question'
    const resolved = m.resolved_at !== null
    const classes = [
      'msg',
      isReply ? 'is-reply' : '',
      m.author_type === 'agent' ? 'is-agent' : '',
      m.automated ? 'is-automated' : '',
      isQuestion ? 'is-question' : '',
      resolved ? 'is-resolved' : '',
      m.author_id === me.user_id ? 'mine' : '',
    ]
      .filter(Boolean)
      .join(' ')

    // Name the recipient the same way the author is named, nickname included:
    // "誰の AI に宛てたのか" is the whole point of the human/agent distinction.
    const to = m.mentions
      .map((id) => {
        const member = members.find((x) => x.user_id === id)
        if (!member) return null
        return m.to_role === 'agent'
          ? agentLabel(member.display_name, member.agent_nickname)
          : member.display_name
      })
      .filter(Boolean)

    return (
      <article key={m.id} className={classes}>
        <div className="msg-head">
          <span className="msg-author">{m.author_label}</span>
          <span className={`pill ${m.author_type}`}>{m.author_type === 'agent' ? 'AI' : '人'}</span>
          {m.automated && (
            <span className="pill warn" title="本人が読んでいない自動返信です">
              自動
            </span>
          )}
          {KIND_LABEL[m.kind] && <span className={`pill ${m.kind}`}>{KIND_LABEL[m.kind]}</span>}
          <span>{m.author_org}</span>
          <span>{timeOf(m.created_at)}</span>
          {to.length > 0 && (
            <span>
              → {to.join(', ')}
              {m.to_role === 'human' && ' 本人'}
            </span>
          )}
          {resolved && <span>✓ 解決済み</span>}
        </div>
        {m.retracted_at ? (
          /* The row stays so the gap is visible. Someone had already received the
             content, and a silently missing message would misrepresent the
             conversation to everyone else reading it later. */
          <div className="msg-body msg-retracted">
            この発言は投稿者によって取り消されました（{timeOf(m.retracted_at)}）
          </div>
        ) : (
          <MessageBody text={m.body} />
        )}
        {!m.retracted_at && (isQuestion || m.author_id === me.user_id) && (
          <div className="msg-actions">
            {isQuestion && !resolved && (
              <button onClick={() => resolve(m.id)}>解決済みにする</button>
            )}
            {m.author_id === me.user_id && <button onClick={() => retract(m.id)}>取り消す</button>}
          </div>
        )}
      </article>
    )
  }

  let lastDay = ''

  return (
    <>
      {error && <div className="error">{error}</div>}
      {notice && (
        <div className="notice">
          {notice}
          <button className="text" onClick={() => setNotice(null)}>
            閉じる
          </button>
        </div>
      )}
      {held && (
        <div className="notice">
          {held}
          <br />
          「共有待ち」タブで内容を確認して、送ってよければ共有してください。
        </div>
      )}

      <div className="row filters">
        {(
          [
            ['all', `全部 ${threads.length}`],
            ['open', `未解決の質問 ${threads.filter((t) => t.root.kind === 'question' && !t.resolved).length}`],
            ['mine', `自分宛 ${threads.filter((t) => [t.root, ...t.replies].some((m) => m.mentions.includes(me.user_id))).length}`],
          ] as [Filter, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            aria-current={filter === id}
            className={filter === id ? 'tonal' : ''}
            onClick={() => setFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="timeline">
        {filtered.length === 0 && <p className="muted">表示する発言がありません。</p>}

        {hidden > 0 && (
          <button className="text" onClick={() => setLimit(limit + 100)}>
            これ以前の {hidden} 件を読む
          </button>
        )}

        {visible.map((t) => {
          const day = dayOf(t.root.created_at)
          const separator = day !== lastDay ? day : null
          lastDay = day

          /*
           * A finished exchange becomes one line. Collapsed in place rather than
           * moved to another tab: the order of a conversation carries meaning, and
           * relocating it only trades "too long" for "where did that go".
           */
          const collapsed = t.resolved && openThread !== t.root.id

          return (
            <div key={t.root.id}>
              {separator && <div className="day-sep">{separator}</div>}
              {collapsed ? (
                <button className="thread-collapsed" onClick={() => setOpenThread(t.root.id)}>
                  <span className="pill done">✓ 解決済み</span>
                  <span className="thread-title">{t.root.body.split('\n')[0]}</span>
                  <span className="muted">
                    {t.replies.length > 0 ? `回答 ${t.replies.length} 件` : '回答なし'} ・{' '}
                    {dayOf(t.at)}
                  </span>
                </button>
              ) : (
                <div className={t.replies.length > 0 ? 'thread' : undefined}>
                  {renderMessage(t.root)}
                  {t.replies.map((r) => renderMessage(r, true))}
                  {t.resolved && (
                    <button className="text" onClick={() => setOpenThread(null)}>
                      このやりとりを畳む
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
        <div ref={bottom} />
      </div>

      <div className="composer">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="メッセージ (Ctrl+Enter で送信)"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void send()
          }}
        />
        <div className="row">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as MessageKind)}
            style={{ width: 'auto' }}
          >
            <option value="chat">通常</option>
            <option value="question">質問</option>
            <option value="answer">回答</option>
            <option value="decision">決定</option>
          </select>
          <select
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            style={{ width: 'auto' }}
            aria-label="宛先"
          >
            <option value="">宛先なし (全員)</option>
            {others.map((m) => [
              <option key={`${m.user_id}:human`} value={`${m.user_id}:human`}>
                {m.display_name} 本人 ({m.org})
              </option>,
              <option key={`${m.user_id}:agent`} value={`${m.user_id}:agent`}>
                {agentLabel(m.display_name, m.agent_nickname)}
              </option>,
              <option key={`${m.user_id}:any`} value={`${m.user_id}:any`}>
                {m.display_name} (人でもAIでも)
              </option>,
            ])}
          </select>
          <button className="primary" disabled={busy || !body.trim()} onClick={send}>
            送信
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          長くなるときは仕様書か添付にして、ここには要点と置き場所を書いてください。
        </p>
      </div>
    </>
  )
}
